import { type CreatedBackup, createBackup } from '~/exports/backup/createBackup'
import {
  type RestoreError,
  type RestoreOutcome,
  restoreBackup,
} from '~/exports/backup/restoreBackup'
import { type ValidationError, validateBackup } from '~/exports/backup/validateBackup'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { setLastBackupAt } from '~/storage/repositories/metaRepository'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { clearBackupReminderDismissal } from './backupReminderService'

/**
 * Backup and restore orchestration.
 *
 * The file-handling half lives here because it touches browser APIs; every
 * decision about integrity, migration, and atomicity lives in `exports/backup/`
 * and is tested without a DOM.
 */

export const APP_VERSION = '0.1.0'

export type BackupCreationError = StorageError

/**
 * Create a backup and record the time.
 *
 * `lastBackupAt` is written ONLY after the artifact has been produced
 * successfully (D-022, D-030). A failed backup must never make the user believe
 * they are protected.
 */
export async function createAndRecordBackup(
  db: SolaDatabase,
  now: Date,
): Promise<Result<CreatedBackup, BackupCreationError>> {
  const created = await createBackup(db, now, { appVersion: APP_VERSION })
  if (!created.ok) return created

  const recorded = await setLastBackupAt(db, now.toISOString())
  if (!recorded.ok) return err(recorded.error)

  /* A dismissal made before this backup must not keep suppressing the reminder
     afterwards — it would go quiet for the wrong reason, which is the one thing
     a reminder cannot afford (D-084). */
  const cleared = await clearBackupReminderDismissal(db)
  if (!cleared.ok) return err(cleared.error)

  return ok(created.value)
}

export type RestoreFailure =
  | { readonly kind: 'invalidBackup'; readonly error: ValidationError }
  | { readonly kind: 'restoreFailed'; readonly error: RestoreError }

/**
 * Validate then replace.
 *
 * Two separate steps on purpose: the caller shows the user what the backup
 * contains and takes an explicit confirmation between them. Nothing is mutated
 * by validation.
 */
export async function validateBackupFiles(parts: readonly Uint8Array[]) {
  return validateBackup(parts)
}

export async function restoreValidatedBackup(
  db: SolaDatabase,
  backup: Awaited<ReturnType<typeof validateBackup>>,
): Promise<Result<RestoreOutcome, RestoreFailure>> {
  if (!backup.ok) return err({ kind: 'invalidBackup', error: backup.error })

  const restored = await restoreBackup(db, backup.backup)
  if (!restored.ok) return err({ kind: 'restoreFailed', error: restored.error })

  return ok(restored.value)
}

export function restoreFailureMessage(failure: RestoreFailure): string {
  if (failure.kind === 'invalidBackup') return failure.error.message
  return failure.error.message
}

/** Read selected files as bytes. Separate so tests can bypass the DOM. */
export async function readFilesAsBytes(files: readonly File[]): Promise<Uint8Array[]> {
  const parts: Uint8Array[] = []
  for (const file of files) {
    parts.push(new Uint8Array(await file.arrayBuffer()))
  }
  return parts
}

/**
 * Hand the artifact to the user.
 *
 * Prefers the Web Share sheet, which is how a file reaches Files on iOS, and
 * falls back to a download link. Both need a complete `Blob` — there is no File
 * System Access API on iOS, which is exactly why the size policy exists.
 */
export async function deliverBackup(
  files: readonly { fileName: string; bytes: Uint8Array }[],
): Promise<void> {
  const blobs = files.map(
    (file) => new File([file.bytes as BlobPart], file.fileName, { type: 'application/zip' }),
  )

  if (typeof navigator !== 'undefined' && typeof navigator.canShare === 'function') {
    if (navigator.canShare({ files: blobs })) {
      try {
        await navigator.share({ files: blobs, title: 'Project Sola backup' })
        return
      } catch (cause: unknown) {
        /* Dismissing the share sheet is a decision, not a failure — the backup
           was created perfectly well — so it is reported as nothing at all.
           Any other share failure falls through to the download link, because
           `canShare` saying yes does not mean the platform can follow through;
           desktop browsers routinely cannot.

           Without this, a user who changed their mind was told their backup had
           failed, on the one feature that protects them from losing everything.
           Found on the export path during Checkpoint 1.7 and fixed there; this
           is the same defect in the older backup path. */
        if (cause instanceof Error && cause.name === 'AbortError') return
      }
    }
  }

  for (const file of blobs) {
    const url = URL.createObjectURL(file)
    try {
      const link = document.createElement('a')
      link.href = url
      link.download = file.name
      link.click()
    } finally {
      // Revoked on the next tick so the click has taken effect.
      setTimeout(() => URL.revokeObjectURL(url), 0)
    }
  }
}
